import { type ReactNode } from 'react';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { FormattedMessage } from 'react-intl';
import { Dialog } from '@understory-io/leaf/dialog';
import { TextLink } from '~/ui/link';
import { Waitlist, type WaitlistTrackingProps } from './waitlist';

type WaitlistDialogProps = WaitlistTrackingProps & {
  /** Whether the dialog is open. Renders nothing when false, so callers can
   *  mount it unconditionally and just flip this. */
  open: boolean;
  onClose: () => void;
  /** When set, the form also offers an "Or request a date" link. */
  requestEventHref?: string;
};

/**
 * The waitlist signup in a responsive dialog — a bottom sheet on mobile, a
 * centered modal on tablet+. Wraps the shared `Waitlist` form with the standard
 * heading, subtitle, and optional "request a date" link, so every surface that
 * opens the waitlist from a trigger shows the exact same thing: the experience
 * booking panel and both checkout date/time steps.
 *
 * The one waitlist that is deliberately NOT a dialog is
 * `AvailabilityEmptyState`'s: when there are no dates at all, the form is the
 * primary content, shown directly rather than behind a trigger.
 */
export function WaitlistDialog({
  open,
  onClose,
  requestEventHref,
  onViewed,
  onSubmitted,
}: WaitlistDialogProps) {
  // Nothing renders while closed, so the form mounts afresh on every open and
  // its `onViewed` fires once per opening.
  if (!open) return null;

  const requestLink = requestEventHref
    ? (chunks: ReactNode) => (
        <TextLink to={requestEventHref} style="body-small-regular">
          {chunks}
        </TextLink>
      )
    : undefined;

  return (
    <Dialog
      size="small"
      title={
        <FormattedMessage
          defaultMessage="Get notified on new dates"
          description="Booking widget waitlist view heading"
        />
      }
      onClose={onClose}
    >
      <Waitlist
        onViewed={onViewed}
        onSubmitted={onSubmitted}
        header={
          <Text
            as="p"
            style="body-small-regular"
            className={css({ color: 'content.secondary' })}
          >
            <FormattedMessage
              defaultMessage="Add your email and we will let you know as soon as new dates are scheduled."
              description="Booking widget waitlist view subtitle"
            />
          </Text>
        }
        footer={
          requestLink ? (
            <Text
              style="body-small-regular"
              className={css({ textAlign: 'center' })}
            >
              <FormattedMessage
                defaultMessage="Or <link>request a date</link>"
                description="Link below the checkout waitlist form offering visitors to request a date instead"
                values={{ link: requestLink }}
              />
            </Text>
          ) : undefined
        }
      />
    </Dialog>
  );
}
